// Pricing service for fetching SOL/USDC exchange rate

interface PriceCache {
  solUsdcPrice: number | null
  timestamp: number
}

let priceCache: PriceCache = {
  solUsdcPrice: null,
  timestamp: 0,
}

const CACHE_DURATION = 60000 // 1 minute cache

/**
 * Fetches SOL/USDC price from CoinGecko API
 * @returns Price of 1 SOL in USDC
 */
export async function fetchSolUsdcPrice(): Promise<number> {
  try {
    // CoinGecko API endpoint for SOL price in USD
    const response = await fetch(
      'https://api.coingecko.com/api/v3/simple/price?ids=solana&vs_currencies=usd'
    )

    if (!response.ok) {
      throw new Error('Failed to fetch SOL price')
    }

    const data = await response.json()
    const price = data.solana?.usd

    if (!price || price <= 0) {
      throw new Error('Invalid price data')
    }

    return price
  } catch (error) {
    console.error('Error fetching SOL/USDC price:', error)
    // Fallback to a reasonable default (e.g., $150)
    return 150
  }
}

/**
 * Gets cached SOL/USDC price or fetches new one if cache expired
 */
export async function getSolUsdcPrice(): Promise<number> {
  const now = Date.now()

  if (
    priceCache.solUsdcPrice === null ||
    now - priceCache.timestamp > CACHE_DURATION
  ) {
    priceCache.solUsdcPrice = await fetchSolUsdcPrice()
    priceCache.timestamp = now
  }

  return priceCache.solUsdcPrice!
}

/**
 * Converts SOL amount to USDC
 */
export async function solToUsdc(solAmount: number): Promise<number> {
  const price = await getSolUsdcPrice()
  return solAmount * price
}

/**
 * Converts USDC amount to SOL
 */
export async function usdcToSol(usdcAmount: number): Promise<number> {
  const price = await getSolUsdcPrice()
  return usdcAmount / price
}

/**
 * Gets $PULSE price in USDC based on SOL price
 * @param priceInSol Price per token in SOL
 * @returns Price per token in USDC
 */
export async function getPulsePriceInUsdc(priceInSol: number): Promise<number> {
  const solPrice = await getSolUsdcPrice()
  return priceInSol * solPrice
}
